/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependdencies
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// UI lib components
import Table, { useTable } from '@ui-kit/organisms/Table';
import { Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import useAccount from '@features/Account/store/useAccount';

// Helpers
import baseConfig from './base-config';

// API & types
import {
  ResourceRowFragmentFragment,
  useListResourcesQuery,
} from '@app-types/blackroom-api';
import useDatadumpFolderIds from '@features/Resource/hooks/useDatadumpFolderIds';

// Types
import { ResourceTableProps, PAGE_SIZE } from './types';
import useColumns from './useColumns';
import useResourceDragAndDrop from './useResourceDragAndDrop';

// Types
import {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_RowVirtualizer,
  MRT_TableOptions,
} from 'mantine-react-table';

// Style
import './styles/index.css';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { vars } from '@app/shared/themes/vars';
import { useResourceTableKeyboardNavigation } from '../ResourcePane/useResourceTableKeyboardNavigation';
import { useContextMenu } from 'mantine-contextmenu';
import { useNavigate } from 'react-router-dom';
import ResourceContextMenu from '../ResourceContextMenu';
import { useResourceFilteredActions } from '../hooks/useResourceFilteredActions';
import useMoveResourceActions from '../ResourcePane/useMoveResourceActions';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function ResourceTable(tableProps: ResourceTableProps) {
  /* ********************************** PROPS ********************************* */

  const {
    columns,
    scope = 'my',
    onSelectResources = () => {},
    onSelectAllResources = () => {},
    onTableReady,
    rowVirtualizerInstanceRef,
    parent,
    selectedResources,
    selectAll,
    requestVariables,
    sorting,
    onSortingChange,
    ...props
  } = tableProps;
  /* ********************************** HOOKS ********************************* */

  // Store
  const workspace = useWorkspace();
  const { isCurrentWorkspaceAdmin } = useAccount();
  const { t } = useTranslation();
  const { showContextMenu } = useContextMenu();
  const navigate = useNavigate();
  const { moveResources, moveAndRankResources } = useMoveResourceActions(
    scope,
    requestVariables,
  );

  // Ref
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const localRowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const effectiveRowVirtualizerInstanceRef =
    rowVirtualizerInstanceRef || localRowVirtualizerInstanceRef;

  const { allowedActions } = useResourceFilteredActions({
    resources: null,
    scope,
  });

  const { dataroomFolderId, datadumpFolderId } = useDatadumpFolderIds();

  // State
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );
  const [globalFilter, setGlobalFilter] = useState<string>();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  // Drag and drop
  const {
    isDropTarget,
    handleDragAutoScroll,
    handleDragExit,
    handleDragEnd,
    handleDragOver,
  } = useResourceDragAndDrop({
    tableContainerRef,
    moveResources,
    moveAndRankResources,
  });

  // API
  const {
    data,
    fetchMore,
    loading: isFetching,
  } = useListResourcesQuery({
    variables: requestVariables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const flatData = useMemo(() => {
    const uniqueRows = uniqBy(
      data?.resources.nodes?.flatMap((node) => node) ?? [],
      'id',
    );

    // Filter by parentId to ensure we only show resources belonging to the current folder context
    const expectedParentId = parent?.id;
    const filteredRows = uniqueRows.filter((resource) => {
      // If parentId is explicitly set, only show resources with matching parentId
      if (expectedParentId !== undefined) {
        return resource.parentId === expectedParentId;
      }
      // If parentId is not set, show resources (this handles root level or other contexts)
      return true;
    });

    if (sorting.length === 0) {
      return orderBy(filteredRows, 'rank');
    }
    return filteredRows;
  }, [data]) as ResourceRowFragmentFragment[];

  // Keyboard navigation hook
  const handleNavigateToFolder = useCallback(
    (folderId: string) => {
      navigate(
        `/workspace/${workspace.id}/resources/${scope}/${encodeURIComponent(folderId)}`,
      );
    },
    [navigate, workspace.id, scope],
  );

  const handleNavigateBack = useCallback(() => {
    // Navigate to the parent folder using parent.parentId
    const parentFolderId = parent?.parentId;

    // Navigate to parent folder or root
    if (parentFolderId) {
      navigate(
        `/workspace/${workspace.id}/resources/${scope}/${encodeURIComponent(parentFolderId)}`,
      );
    } else {
      // No parent folder, go to root
      navigate(`/workspace/${workspace.id}/resources/${scope}`);
    }
  }, [navigate, workspace.id, scope, parent]);

  // Table
  const columnDefs = useColumns({ columns, scope, tableProps, hoveredRowId });
  const tableConfig: MRT_TableOptions<ResourceRowFragmentFragment> = {
    ...baseConfig,
    columns: columnDefs.columns,
    data: flatData,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      style: {
        scrollbarColor: `${vars.colors.blackroomBlack} ${vars.colors.blackroomWhite}`,
        scrollbarWidth: 'thin',
      },
      onScroll: (event: React.UIEvent<HTMLDivElement>) => {
        setTimeout(() => {
          const element = event.target as HTMLDivElement;
          const isNearBottom =
            element.scrollHeight - element.scrollTop <=
            element.clientHeight + 50;
          const isNearTop = element.scrollTop <= 50;
          if (isNearBottom && !isNearTop) {
            fetchMoreOnBottomReached(element);
          }
        }, 500);
      },
    },
    mantineSelectAllCheckboxProps: {
      checked: isSelectAllChecked,
      radius: 'xs',
      size: 'sm',
      __vars: {
        '--mantine-cursor-type': 'pointer',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-select': {
        visibleInShowHideMenu: false,
      },
      'mrt-row-drag': {
        header: '',
      },
    },
    meta: {
      context: { scope, dataroomFolderId, datadumpFolderId },
    },
    rowVirtualizerInstanceRef: effectiveRowVirtualizerInstanceRef,
    getRowId: (originalRow) => originalRow.id,
    mantineRowDragHandleProps: ({ table }) => ({
      onDrag: (event: React.DragEvent) => {
        handleDragAutoScroll(event);
        const { hoveredRow, draggingRow } = table.getState();
        handleDragOver(hoveredRow, draggingRow);
      },
      onDragExit: handleDragExit,
      onDragEnd: async () => {
        const { hoveredRow, draggingRow } = table.getState();
        await handleDragEnd(hoveredRow, draggingRow);
      },
    }),
    mantineTableBodyRowProps: ({ row }) => {
      const isFlashing = isResourceFlashing(row.original.id);
      const rowIsDropTarget = isDropTarget(row.original.id);

      // Determine background color based on state
      let backgroundColor = undefined;
      if (isFlashing) {
        backgroundColor = vars.colors.blackroomBlue[2];
      } else if (rowIsDropTarget) {
        // Visual feedback for "move into folder" mode
        backgroundColor = vars.colors.blackroomBlue[1];
      }

      return {
        onClick: (event: React.MouseEvent) => {
          handleRowClick(row.original.id, event);
        },
        onMouseEnter: () => {
          setHoveredRowId(row.original.id);
        },
        onMouseLeave: () => {
          setHoveredRowId(null);
        },
        onContextMenu: showContextMenu(
          (close) => {
            const contextResources = uniqBy(
              [...selectedResources, row.original],
              'id',
            );
            return (
              <ResourceContextMenu
                resources={contextResources}
                allowedActions={allowedActions}
                actionOptions={{
                  selectAll,
                  parentId: parent?.id,
                }}
                onClose={() => {
                  close();
                }}
                renderWithoutTarget
              />
            );
          },
          {
            styles: {
              root: {
                borderRadius: vars.radius.md,
                padding: '0.25rem',
              },
            },
          },
        ),
        style: {
          borderTop: 'none',
          height: 60,
          backgroundColor,
          transition: rowIsDropTarget ? 'none' : 'background-color 0.3s ease',
          cursor: 'pointer',
          // Add visual emphasis for drop target
          ...(rowIsDropTarget && {
            outline: `2px solid ${vars.colors.blackroomBlue[5]}`,
            outlineOffset: '-2px',
          }),
        },
      };
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange,
    onRowSelectionChange: setRowSelection,
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowVirtualization: true,
    enableExpanding: true,
    manualSorting: true,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    renderEmptyRowsFallback: () => (
      <Stack align="center" justify="center" py="xl" gap="xs">
        <Text c="dimmed" fz="sm">
          {!isCurrentWorkspaceAdmin && scope === 'my'
            ? t('Resource:PageResources.noData.noPermissionsMessage')
            : t('Resource:PageResources.noData.message')}
        </Text>
      </Stack>
    ),
    mantineTableProps: {
      highlightOnHover: false,
    },
    ...props,
    initialState: {
      ...props.initialState,
      columnVisibility: columnDefs.visibility,
    },
    state: {
      columnFilters,
      globalFilter,
      sorting,
      isLoading: isFetching && !data?.resources.nodes,
      rowSelection,
      columnOrder: [
        'mrt-row-expand',
        'mrt-row-drag',
        'mrt-row-select',
        ...columnDefs.columns.map((col) => col.accessorKey as string),
      ],
      ...props.state,
    },
  };

  const table = useTable(tableConfig);

  const { handleRowClick, isResourceFlashing } =
    useResourceTableKeyboardNavigation({
      table,
      rowVirtualizerInstanceRef: effectiveRowVirtualizerInstanceRef,
      onNavigateToFolder: handleNavigateToFolder,
      onNavigateBack: handleNavigateBack,
      canNavigateBack: !!parent, // Can navigate back if we're in a folder
      currentFolderId: parent?.id || null, // Current folder ID for paste operations
      scope,
      requestVariables,
    });

  /* ***************************** LOCAL VARIABLES **************************** */

  const totalCount = data?.resources.pageInfo?.totalCount ?? 0;
  const currentOffset = data?.resources.pageInfo?.offset ?? 0;
  const totalFetched = currentOffset + PAGE_SIZE;

  /* ******************************** CALLBACKS ******************************* */

  const refetch = useCallback(() => {
    fetchMore({
      variables: { ...requestVariables, offset: currentOffset + PAGE_SIZE },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prevResult;

        return {
          resources: {
            ...fetchMoreResult.resources,
            nodes: [
              ...(prevResult.resources.nodes ?? []),
              ...(fetchMoreResult.resources.nodes ?? []),
            ],
          },
        };
      },
    });
  }, [currentOffset, requestVariables]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        if (!isFetching && totalFetched < totalCount) {
          refetch();
        }
      }
    },
    [refetch, isFetching, totalFetched, totalCount, currentOffset],
  );

  /* ****************************** SIDE-EFFECTS ****************************** */

  useEffect(() => {
    onTableReady?.(table);
  }, [table]);

  useEffect(() => {
    if (effectiveRowVirtualizerInstanceRef.current) {
      try {
        effectiveRowVirtualizerInstanceRef.current.scrollToIndex(0);
      } catch (e) {
        console.error(e);
      }
    }
  }, [sorting, columnFilters, globalFilter]);

  useEffect(() => {
    const selectedResourceIds = Object.keys(rowSelection);
    const selectedResources = flatData.filter((r) =>
      selectedResourceIds.includes(r.id),
    );
    const isAllRowsSelected = table.getIsAllRowsSelected();

    onSelectResources(selectedResources);

    if (isAllRowsSelected) {
      setIsSelectAllChecked(true);
    } else {
      setIsSelectAllChecked(false);
    }
  }, [
    rowSelection,
    props.state?.rowSelection,
    table.getIsAllRowsSelected(),
    flatData,
  ]);

  useEffect(() => {
    setRowSelection(props.state?.rowSelection ?? {});
  }, [props.state?.rowSelection]);

  useEffect(() => {
    if (table && isSelectAllChecked) {
      table.toggleAllRowsSelected(true);
    }
    onSelectAllResources(isSelectAllChecked);
  }, [flatData.length, isSelectAllChecked]);

  // Serialize query parameters to create a stable string representation for dependency comparison
  // This ensures the useEffect only runs when the actual content of our query parameters changes,
  // not just when their object references change (which happens on every render)
  const serializedQueryParams = JSON.stringify({
    parent,
    scope,
    sorting,
    globalFilter,
    columnFilters,
    workspaceId: workspace.id,
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [serializedQueryParams]);

  /* ******************************** RENDERING ******************************* */

  return <Table table={table} unstyledShell />;
}

export default ResourceTable;
